/**
 * `<ComponentGate/>` — per-team component-toggle wrapper (#173 PR-A4).
 *
 * Mirrors `Depends(require_component_for_team / for_cycle)` on the
 * backend (PR-A3a) but for the SPA. Reads `useTeamFeature(teamId, key)`
 * and renders one of three branches:
 *
 *   * **Loading** (`enabled === undefined`) — render `children`. The
 *     fast path: a flicker of the off-card during the network round-
 *     trip would feel worse than the brief mount-then-replace, and
 *     the backend gate (404) would have caught any actual access
 *     attempt before the SPA had time to paint anyway.
 *   * **Enabled** (`enabled === true`) — render `children`.
 *   * **Disabled** (`enabled === false`) — render `fallback` (default:
 *     `<ComponentOffCard/>`) OR `null` when `ifOff="hide"`.
 *
 * Two of the three usage shapes from the architectural plan §3.2 are
 * supported here:
 *
 *   * **Whole-route gate.**
 *
 *     ```tsx
 *     <Route
 *       path="/teams/:tid/insights/mood"
 *       element={
 *         <ComponentGate component="live_mood" teamId={tid}>
 *           <MoodPage />
 *         </ComponentGate>
 *       }
 *     />
 *     ```
 *
 *   * **In-page conditional.** Wrap component-specific cards on a
 *     surface that has both always-on and component-gated parts.
 *
 *     ```tsx
 *     <OutcomesPage>
 *       <AlwaysOnSummary />
 *       <ComponentGate component="forecast" teamId={tid} ifOff="hide">
 *         <ForecastCard />
 *       </ComponentGate>
 *     </OutcomesPage>
 *     ```
 *
 * **Panel-section gate (third shape) is INTENTIONALLY DEFERRED.**
 * The plan doc §3.2 third shape wraps `<PersistentRightPanel/>`
 * sections, but `web/src/components/PersistentRightPanel.tsx` does
 * not yet exist — it ships in the IA rework's PR 1. This component
 * supports the panel-section variant via the same `ifOff="hide"`
 * branch already; what's deferred is the `<PersistentRightPanel/>`
 * scaffold itself + the per-section integration. PR-A4.5 (post-IA-
 * rework) will plumb `<ComponentGate ifOff="hide">` into each
 * candidate section per IA rework §9.2's suppression rules.
 */

import type { ReactNode } from "react";

import { useTeamFeature } from "../api/useTeamFeature";
import { ComponentOffCard } from "./ComponentOffCard";

interface Props {
  /** The `component_key` from `COMPONENT_REGISTRY`. */
  component: string;
  /** The team whose toggle state drives the gate. */
  teamId: string;
  /** What to render when the component is enabled. */
  children: ReactNode;
  /** What to render when the component is disabled. Defaults to
   * `<ComponentOffCard/>`. Passing your own fallback is useful for
   * surfaces where a card-shaped off-state would be visually wrong
   * (e.g., a sidebar item — pass `null` or a tiny "off" pill). */
  fallback?: ReactNode;
  /** Behaviour when off:
   * - `"render-fallback"` (default) — show `fallback` (or
   *   `<ComponentOffCard/>` if no fallback was passed).
   * - `"hide"` — render `null`. Useful for in-page sections where
   *   the off-state should disappear entirely rather than show a
   *   placeholder. */
  ifOff?: "render-fallback" | "hide";
}

export function ComponentGate({
  component,
  teamId,
  children,
  fallback,
  ifOff = "render-fallback",
}: Props) {
  const enabled = useTeamFeature(teamId, component);

  // Loading-or-undefined path: render children optimistically. The
  // backend dep is the load-bearing gate; the SPA is decoration.
  if (enabled === undefined) {
    return <>{children}</>;
  }

  if (enabled) {
    return <>{children}</>;
  }

  if (ifOff === "hide") {
    return null;
  }

  return <>{fallback ?? <ComponentOffCard componentKey={component} teamId={teamId} />}</>;
}
